import { defineAsyncComponent } from 'vue';
import type { RouteRecordRaw } from 'vue-router';
import FullLayout from '@/layouts/full/FullLayout.vue';
import BlankLayout from '@/layouts/blank/BlankLayout.vue';

/**
 * A fila (`/ticket`) e o detalhe (`/ticket/:id`) usam `ConsoleLayout` DIRETO,
 * como os Relatórios do mesmo módulo: as telas são 100% design system console
 * (specs 2026-09-05 da fila, decisão 7, e do detalhe, decisão 4 — zero Vuetify
 * na página) e o `FullLayout` traria a moldura Vuetify por fora.
 *
 * Assíncrono porque um import estático puxaria o CSS do DS para o bundle de
 * entrada (mesma razão do `router/ticketReports.ts`).
 */
const ConsoleLayout = defineAsyncComponent(() => import('@/layouts/console/ConsoleLayout.vue'));

/** Rotas internas do Service Desk (autenticadas). */
export const ticketRoutes: RouteRecordRaw[] = [
  {
    /**
     * ⚠ Registro PRÓPRIO para o índice, e ANTES do bloco `FullLayout` abaixo:
     * os dois têm o path `/ticket`, e entre matchers de mesma pontuação o
     * vue-router responde pelo PRIMEIRO inserido. O bloco de baixo não tem
     * filho `''`, então só as sub-rotas (`novo`, `:id`, `admin/*`) caem nele.
     *
     * Trocar a ordem NÃO dá erro: `/ticket` passa a casar o `FullLayout` sem
     * filho nenhum e a tela abre em branco (medido com o `resolve()` do
     * vue-router em 05/09/2026, nas duas ordens).
     *
     * ⚠ O detalhe (`:id`) mora AQUI, e não no bloco `FullLayout` abaixo: ele
     * também é 100% console (spec do detalhe, decisão 4). Entre dois matchers
     * de mesma pontuação vence o PRIMEIRO inserido — deixar uma cópia do
     * `:id(\d+)` lá embaixo faria a rota abrir a tela antiga sem erro nenhum.
     */
    path: '/ticket',
    component: ConsoleLayout,
    meta: { requiresAuth: true, title: 'Service Desk - Hub AtlanGlobal' },
    children: [
      {
        path: '',
        name: 'TicketList',
        component: () => import('@/views/Ticket/fila/TicketFilaPage.vue'),
        // free-for-auth: qualquer autenticado vê os próprios chamados (backend filtra)
        meta: {
          requiresAuth: true,
          title: 'Chamados',
          breadcrumb: 'Service Desk / Chamados',
          freeForAuth: true,
        },
      },
      {
        path: ':id(\\d+)',
        name: 'TicketDetail',
        component: () => import('@/views/Ticket/detalhe/TicketDetalhePage.vue'),
        props: true,
        meta: { requiresAuth: true, title: 'Chamado', breadcrumb: 'Detalhe', freeForAuth: true },
      },
      {
        path: 'novo',
        name: 'TicketNew',
        // Catálogo de abertura: cards por área abrível; área com sub-áreas abre
        // um dropdown ancorado no próprio card (antes era modal centralizado).
        component: () => import('@/views/Ticket/abertura/TicketAberturaCatalogo.vue'),
        meta: { requiresAuth: true, title: 'Abrir chamado', breadcrumb: 'Abrir chamado', freeForAuth: true },
      },
      {
        // :entityId = a área escolhida no catálogo; o chamado cai nela.
        path: 'novo/:entityId(\\d+)',
        name: 'TicketFormRender',
        component: () => import('@/views/Ticket/abertura/TicketAberturaForm.vue'),
        props: (route) => ({ entityId: route.params.entityId }),
        meta: { requiresAuth: true, title: 'Abrir chamado', breadcrumb: 'Formulário', freeForAuth: true },
      },
    ],
  },
  {
    path: '/ticket',
    component: FullLayout,
    meta: { requiresAuth: true, title: 'Service Desk - Hub AtlanGlobal' },
    children: [
      // A abertura (`novo` e `novo/:entityId`) subiu para o bloco
      // `ConsoleLayout` acima em 2026-09-06, pelo mesmo motivo do detalhe: as
      // telas novas são console e o `FullLayout` traria a moldura Vuetify por
      // fora. ⚠ As cópias daqui tiveram de SAIR: entre matchers de mesma
      // pontuação vence o primeiro inserido, então deixá-las faria uma rota
      // morta com cara de viva — sem erro, só a tela antiga aparecendo.
      // O detalhe (`:id`) subiu para o bloco `ConsoleLayout` acima em
      // 2026-09-05 — a tela nova (`detalhe/TicketDetalhePage.vue`) é console e
      // não pode ficar dentro da moldura Vuetify. `TicketDetail.vue` continua
      // no disco de propósito: a remoção vem depois, com o inventário de ações
      // na mão (spec §4).
      // Relatórios: movidos para o shell console em router/ticketReports.ts
      // (mesmo path /ticket/relatorios — permissões preservadas).
      // Contas internas (Customer Success) removidas em 2026-07-30 — módulo desativado.
      {
        path: 'admin/entidades',
        name: 'TicketEntitiesAdmin',
        component: () => import('@/views/Ticket/admin/EntitiesAdmin.vue'),
        meta: { title: 'Entidades de atendimento', breadcrumb: 'Entidades' },
      },
      {
        path: 'admin/sla',
        name: 'TicketSlaAdmin',
        component: () => import('@/views/Ticket/admin/SlaAdmin.vue'),
        meta: { title: 'Admin de SLA', breadcrumb: 'SLA' },
      },
      {
        path: 'admin/forms',
        name: 'TicketFormsAdmin',
        component: () => import('@/views/Ticket/admin/FormsList.vue'),
        meta: { title: 'Formulários', breadcrumb: 'Formulários' },
      },
      {
        path: 'admin/parametros',
        name: 'TicketParametrosAdmin',
        component: () => import('@/views/Ticket/admin/ParametrosAdmin.vue'),
        meta: { title: 'Parâmetros', breadcrumb: 'Parâmetros' },
      },
      {
        path: 'admin/forms/:id(\\d+)',
        name: 'TicketFormBuilder',
        component: () => import('@/views/Ticket/admin/FormBuilder.vue'),
        props: true,
        meta: { title: 'Editor de formulário', breadcrumb: 'Editor' },
      },
    ],
  },
];

/** Rotas PÚBLICAS do Service Desk (BlankLayout, sem login). */
export const ticketPublicRoutes: RouteRecordRaw[] = [
  {
    path: '/desk/form/:slug',
    component: BlankLayout,
    meta: { requiresAuth: false, title: 'Abertura de chamado' },
    children: [
      {
        path: '',
        name: 'DeskPublicForm',
        component: () => import('@/views/Ticket/public/PublicTicketForm.vue'),
        meta: { requiresAuth: false, title: 'Abertura de chamado' },
      },
    ],
  },
  {
    path: '/desk/track/:token',
    component: BlankLayout,
    meta: { requiresAuth: false, title: 'Acompanhar chamado' },
    children: [
      {
        path: '',
        name: 'DeskPublicTracking',
        component: () => import('@/views/Ticket/public/PublicTicketTracking.vue'),
        meta: { requiresAuth: false, title: 'Acompanhar chamado' },
      },
    ],
  },
  {
    path: '/desk/p/:entitySlug',
    component: BlankLayout,
    meta: { requiresAuth: false, title: 'Abrir chamado' },
    children: [
      {
        path: '',
        name: 'DeskPublicEntityForm',
        component: () => import('@/views/Ticket/public/PublicEntityForm.vue'),
        meta: { requiresAuth: false, title: 'Abrir chamado' },
      },
    ],
  },
];

export default ticketRoutes;
